import { useState } from "react";
import {
  Loader2, ArrowRight, ArrowLeft, Eye, EyeOff, Mail, Lock, ShieldCheck,
  Candy, MessageCircleQuestion, Calculator, Compass,
} from "lucide-react";
import { useUser } from "@/context/UserContext";
import { useLang, LanguageToggle } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const RED = "#B0271C";

const Feature = ({ icon: Icon, title, children }) => (
  <div className="flex flex-col items-center text-center px-2">
    <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center shadow-[0_2px_10px_rgba(74,49,34,0.08)] mb-3">
      <Icon className="w-7 h-7" style={{ color: RED }} />
    </div>
    <h3 className="font-heading font-bold text-sm tracking-wide uppercase mb-1.5" style={{ color: RED }}>{title}</h3>
    <p className="text-sm text-[#6E5C4A] leading-relaxed max-w-[15rem]">{children}</p>
  </div>
);

const AuthScreen = () => {
  const { login, forgotPassword } = useUser();
  const { t } = useLang();
  const [mode, setMode] = useState("login"); // login | forgot
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);

  const loginMetGoogle = () => {
    // Onthoud de huidige pagina zodat de gebruiker na Google-login daar terugkeert.
    const path = window.location.pathname + window.location.search;
    if (!path.startsWith("/voorwaarden") && !path.startsWith("/privacy")) {
      sessionStorage.setItem("zk_return_to", path);
    }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async () => {
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email.trim().toLowerCase(), password);
      } else if (mode === "forgot") {
        const r = await forgotPassword(email.trim().toLowerCase());
        toast.success(r?.data?.melding || "Als het e-mailadres bekend is, is een herstelmail verstuurd.");
        setMode("login");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Er ging iets mis. Controleer je gegevens.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF3E8] flex flex-col overflow-x-hidden">
      {/* Topbar */}
      <header className="w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: RED }}>
              <Compass className="w-6 h-6 text-white" />
            </span>
            <span className="font-heading font-extrabold text-xl text-[#2B211A]">Zoetwaren Kompas</span>
          </div>
          <LanguageToggle />
        </div>
      </header>

      {/* Hero: Variant B — kompas centraal, rustig kennisportaal */}
      <main className="flex-1">
        <div className="relative overflow-hidden">
          <section className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 flex flex-col items-center pt-2 lg:pt-6 pb-16 lg:pb-20">
            <img src="/assets/kompas-hero.webp" alt="Kompas met subtiele zoetwaren-accenten"
              className="w-full max-w-[230px] sm:max-w-lg mx-auto rounded-3xl shadow-[0_20px_50px_-25px_rgba(74,49,34,0.35)] animate-fade-up" />
            <h1 className="font-heading font-extrabold text-center leading-tight text-3xl sm:text-5xl mt-5 sm:mt-8 animate-fade-up" lang="nl">
              <span className="text-[#2B211A]">Zoetwaren </span>
              <span style={{ color: RED }}>Kompas</span>
            </h1>
            <div className="h-1.5 w-16 rounded-full mt-3 mb-3 sm:mt-4 sm:mb-4" style={{ backgroundColor: "#B9772B" }} />
            <p className="text-base sm:text-xl text-[#4A3B2E] text-center leading-relaxed max-w-lg animate-fade-up">
              Jouw kompas in cao, salaris, functie en arbo
            </p>

            {/* Login card */}
            <div className="mt-6 sm:mt-10 flex justify-center animate-fade-up min-w-0 w-full">
              <div className="w-full max-w-md bg-white rounded-[1.75rem] shadow-[0_20px_60px_-20px_rgba(74,49,34,0.35)] border border-[#F0E6D6] p-6 sm:p-9">

              {mode === "forgot" ? (
                <>
                  <button data-testid="back-to-login" onClick={() => setMode("login")}
                    className="text-sm text-[#8A7660] hover:text-[#2B211A] inline-flex items-center gap-1 mb-3">
                    <ArrowLeft className="w-4 h-4" /> {t("forgot.back")}
                  </button>
                  <h2 className="font-heading text-3xl font-extrabold text-[#2B211A]">{t("forgot.title")}</h2>
                  <p className="text-[#8A7660] text-sm mt-1 mb-6">{t("forgot.subtitle")}</p>
                </>
              ) : (
                <>
                  <h2 className="font-heading text-3xl font-extrabold text-[#2B211A]">{t("login.title")}</h2>
                  <p className="text-[#8A7660] text-sm mt-1 mb-6">{t("login.subtitle")}</p>
                </>
              )}

              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-[#4A3B2E] font-semibold">{t("login.email")}</Label>
                  <div className="relative">
                    <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#B9A78C]" />
                    <Input data-testid="auth-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="naam@voorbeeld.nl" className="pl-10 h-12 bg-[#FBF7F0] border-[#EADFCB] rounded-xl"
                      onKeyDown={(e) => mode === "forgot" && e.key === "Enter" && submit()} />
                  </div>
                </div>

                {mode !== "forgot" && (
                  <div className="space-y-1.5">
                    <Label className="text-[#4A3B2E] font-semibold">{t("login.password")}</Label>
                    <div className="relative">
                      <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#B9A78C]" />
                      <Input data-testid="auth-password" type={showPw ? "text" : "password"} value={password}
                        onChange={(e) => setPassword(e.target.value)} placeholder="Minimaal 8 tekens"
                        className="pl-10 pr-10 h-12 bg-[#FBF7F0] border-[#EADFCB] rounded-xl"
                        onKeyDown={(e) => e.key === "Enter" && submit()} />
                      <button type="button" data-testid="toggle-password" onClick={() => setShowPw((v) => !v)}
                        aria-label={showPw ? "Wachtwoord verbergen" : "Wachtwoord tonen"}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#B9A78C] hover:text-[#5B4636] transition-colors">
                        {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <Button data-testid="auth-submit" onClick={submit} disabled={busy}
                className="w-full mt-6 h-12 rounded-xl text-white font-semibold text-base hover:brightness-95 transition-all"
                style={{ backgroundColor: RED }}>
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                {mode === "login" ? t("login.submit") : t("forgot.submit")}
              </Button>

              {mode === "login" && (
                <div className="mt-4 text-center">
                  <button data-testid="to-forgot" onClick={() => setMode("forgot")}
                    className="text-sm font-semibold underline hover:no-underline" style={{ color: RED }}>
                    {t("login.forgot")}
                  </button>
                </div>
              )}

              {mode === "login" && (
                <>
                  <div className="my-6 flex items-center gap-3">
                    <span className="h-px flex-1 bg-[#EADFCB]" />
                    <span className="text-xs text-[#B9A78C] uppercase tracking-wider">{t("login.or")}</span>
                    <span className="h-px flex-1 bg-[#EADFCB]" />
                  </div>
                  <Button data-testid="google-login-btn" variant="outline" onClick={loginMetGoogle}
                    className="w-full h-12 rounded-xl border-[#EADFCB] bg-white text-[#2B211A] hover:bg-[#FBF7F0] gap-2 font-semibold">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
                      <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z" />
                    </svg>
                    {t("login.google")}
                  </Button>
                  <p className="mt-4 text-xs text-[#8A7660] text-center leading-relaxed">{t("login.google.note")}</p>
                  <p className="mt-4 text-sm font-semibold text-center leading-relaxed" style={{ color: RED }}>
                    {t("login.noaccount")}
                  </p>
                </>
              )}
            </div>
            </div>
            <div className="mt-8 inline-flex items-center gap-3 rounded-2xl bg-[#F1E4CE] px-4 py-3 animate-fade-up">
              <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: "#B9772B" }}>
                <ShieldCheck className="w-5 h-5 text-white" />
              </span>
              <span className="text-sm font-semibold text-[#5B4636] leading-tight">
                AVG-conform<br /><span className="font-normal text-[#7A6650]">vertrouwen als standaard</span>
              </span>
            </div>
        </section>

        </div>

        {/* Features */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 lg:py-16 grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 lg:gap-6">
          <Feature icon={Candy} title="Kennisbank">Actuele cao, wet- en regelgeving en beleid op één plek.</Feature>
          <Feature icon={MessageCircleQuestion} title="Vraag &amp; antwoord">Snel antwoord op jouw arbeidsvoorwaardenvragen — je ziet altijd waar het staat.</Feature>
          <Feature icon={Calculator} title="Salarischeck">Inzicht in je loon, toeslagen en vergoedingen.</Feature>
          <Feature icon={ShieldCheck} title="Betrouwbaar">Bronvermelding, status en vertrouwensniveau voor iedere gebruiker.</Feature>
        </section>
      </main>

      {/* Footer */}
      <footer className="relative bg-[#2E211B] text-white pb-[env(safe-area-inset-bottom)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8 pb-3 sm:pb-3 flex flex-col sm:flex-row items-center gap-5 sm:gap-8">
          <div className="flex items-center gap-3">
            <span className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center border border-white/15">
              <Compass className="w-6 h-6 text-white" />
            </span>
            <div>
              <p className="font-heading font-extrabold text-lg leading-tight">Zoetwaren Kompas</p>
              <p className="text-white/60 text-sm italic">De juiste richting in arbeidsvoorwaarden</p>
            </div>
          </div>
          <span className="hidden sm:block w-px h-10 bg-white/15" />
          <div className="flex items-center gap-2.5 text-white/70 text-sm">
            <Lock className="w-4 h-4" />
            Voor werknemers en OR-leden in de zoetwarensector.
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pb-24 sm:pb-8 pt-4 border-t border-white/10">
          <p data-testid="login-disclaimer" className="text-white/50 text-xs leading-relaxed max-w-2xl">
            Zoetwaren Kompas kan fouten maken. Controleer belangrijke informatie altijd aan de hand van de cao, wetgeving, het functiehandboek of andere officiële bronnen.
          </p>
          <p className="text-white/50 text-xs mt-1.5">
            <a href="/over" className="underline hover:text-white/80">Over Zoetwaren Kompas</a>
            <span className="mx-1.5">·</span>
            <a href="/voorwaarden" className="underline hover:text-white/80">Algemene Voorwaarden</a>
            <span className="mx-1.5">·</span>
            <a href="/privacy" className="underline hover:text-white/80">Privacyverklaring</a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AuthScreen;
